"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { SectionHeader } from "fansunited-sports-ui";
import type { FUSportsLineupMember, FUSportsCompetitorLineup } from "fansunited-data-layer";
import { MatchLineupFieldPlayerMarker } from "./MatchLineupFieldPlayerMarker";
import { MatchLineupFieldFullPitch } from "./MatchLineupFieldFullPitch";
import type { PlayerEventsMap } from "./playerEventsHelper";
export interface MatchLineupFieldProps {
  homeTeamName: string;
  awayTeamName: string;
  homeTeamCode?: string;
  awayTeamCode?: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeTeamColor?: string;
  awayTeamColor?: string;
  homeTeamCountryCode?: string;
  homeTeamCountryFlag?: string;
  homeTeamPrimaryColor?: string;
  homeTeamSecondaryColor?: string;
  awayTeamCountryCode?: string;
  awayTeamCountryFlag?: string;
  awayTeamPrimaryColor?: string;
  awayTeamSecondaryColor?: string;
  homeLineup: FUSportsCompetitorLineup;
  awayLineup: FUSportsCompetitorLineup;
  homeHasPositions: boolean;
  awayHasPositions: boolean;
  /** For mobile controlled mode - which team is currently active */
  activeTeam?: "home" | "away";
  /** Map of player ID to their events (goals, cards, subs) */
  playerEventsMap: PlayerEventsMap;
}

/**
 * Position players on the horizontal field
 */
const PositionedPlayers: React.FC<{
  lineup: FUSportsCompetitorLineup;
  color: string;
  isAway: boolean;
  playerEventsMap: PlayerEventsMap;
}> = t0 => {
  const $ = _c(7);
  const {
    lineup,
    color,
    isAway,
    playerEventsMap
  } = t0;
  let t1;
  if ($[0] !== color || $[1] !== isAway || $[2] !== lineup.starters || $[3] !== playerEventsMap) {
    const starters = lineup.starters.filter(_temp);
    const columns = new Map();
    starters.forEach(player => {
      const col = player.positionX;
      if (!columns.has(col)) {
        columns.set(col, []);
      }
      columns.get(col).push(player);
    });
    columns.forEach(_temp3);
    const sortedColumns = Array.from(columns.keys()).sort(_temp4);
    const columnCount = sortedColumns.length;
    t1 = sortedColumns.map((posX, colIndex) => {
      const players_0 = columns.get(posX);
      let xPercent;
      if (isAway) {
        xPercent = 94 - colIndex / Math.max(columnCount - 1, 1) * 40;
      } else {
        xPercent = 6 + colIndex / Math.max(columnCount - 1, 1) * 40;
      }
      return players_0.map((player_0, idx) => {
        const count = players_0.length;
        const yPercent = 12 + (idx + 0.5) / count * 76;
        return <div key={player_0.id} className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{
          left: `${xPercent}%`,
          top: `${yPercent}%`
        }}><MatchLineupFieldPlayerMarker player={player_0} color={color} events={playerEventsMap.get(player_0.id)} /></div>;
      });
    });
    $[0] = color;
    $[1] = isAway;
    $[2] = lineup.starters;
    $[3] = playerEventsMap;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * Desktop version - Horizontal full pitch with both teams
 */
export const MatchLineupFieldDesktop: React.FC<MatchLineupFieldProps> = t0 => {
  const $ = _c(26);
  const {
    homeTeamName,
    awayTeamName,
    homeTeamColor: t1,
    awayTeamColor: t2,
    homeLineup,
    awayLineup,
    homeHasPositions,
    awayHasPositions,
    playerEventsMap
  } = t0;
  const homeTeamColor = t1 === undefined ? "#1e40af" : t1;
  const awayTeamColor = t2 === undefined ? "#dc2626" : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SectionHeader entity={{
      name: "Formation",
      styles: {
        typography: {
          name: "text-sm uppercase font-semibold text-foreground"
        }
      }
    }} styles={{
      container: "mb-2"
    }} />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = homeLineup.formation && `(${homeLineup.formation})`;
  let t5;
  if ($[1] !== homeTeamName || $[2] !== t4) {
    t5 = <span className="text-foreground">{homeTeamName} {t4}</span>;
    $[1] = homeTeamName;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = awayLineup.formation && `(${awayLineup.formation})`;
  let t7;
  if ($[4] !== awayTeamName || $[5] !== t6) {
    t7 = <span className="text-foreground">{awayTeamName} {t6}</span>;
    $[4] = awayTeamName;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t7) {
    t8 = <div className="flex justify-between mb-2 text-xs font-semibold">{t5}{t7}</div>;
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== homeHasPositions || $[11] !== homeLineup || $[12] !== homeTeamColor || $[13] !== playerEventsMap) {
    t9 = homeHasPositions && <PositionedPlayers lineup={homeLineup} color={homeTeamColor} isAway={false} playerEventsMap={playerEventsMap} />;
    $[10] = homeHasPositions;
    $[11] = homeLineup;
    $[12] = homeTeamColor;
    $[13] = playerEventsMap;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== awayHasPositions || $[16] !== awayLineup || $[17] !== awayTeamColor || $[18] !== playerEventsMap) {
    t10 = awayHasPositions && <PositionedPlayers lineup={awayLineup} color={awayTeamColor} isAway={true} playerEventsMap={playerEventsMap} />;
    $[15] = awayHasPositions;
    $[16] = awayLineup;
    $[17] = awayTeamColor;
    $[18] = playerEventsMap;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <MatchLineupFieldFullPitch>{t9}{t10}</MatchLineupFieldFullPitch>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <div>{t3}<div className="bg-card rounded-md shadow-md overflow-hidden border border-border p-3">{t8}{t11}</div></div>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
function _temp(p) {
  return p.positionX && p.positionY;
}
function _temp2(a, b) {
  return (a.positionY || 0) - (b.positionY || 0);
}
function _temp3(players) {
  return players.sort(_temp2);
}
function _temp4(a_0, b_0) {
  return a_0 - b_0;
}